import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api-client';
import { useLedgerReload } from '../lib/use-ledger';
import { applyTableQuery, nextSort, type ColumnSort } from '../lib/list-query';
import { Button } from '../components/ui/Button';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../components/ui/ColumnSearch';
import { useNotice } from '../components/ui/Notice';
import { ContextPanel, ExcelButton, PageHead, SortHeader } from '../components/ui/chrome';
import { Input } from '../components/ui/Input';

interface Job {
  key: string;
  name: string;
  cadence: string;
  lastRunAt?: string;
  lastOk?: boolean;
  lastMessage?: string;
  lastResult?: { error?: string };
  running?: boolean;
}

function shownMessage(job: Job): string {
  if (job.lastMessage) return job.lastMessage;
  return job.lastResult?.error || '';
}

function failed(job: Job): boolean {
  if (job.lastOk === false) return true;
  return Boolean(job.lastResult?.error);
}

export function JobsPage() {
  const notice = useNotice();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [logJob, setLogJob] = useState<Job | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const [logError, setLogError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ColumnSort | null>(null);
  const columnSearch = useColumnSearch();
  const logRef = useRef<HTMLPreElement>(null);
  const polling = Object.values(busy).some(Boolean);
  const query = useQuery({
    queryKey: ['jobs'],
    queryFn: () => apiGet<{ jobs: Job[] }>('/api/jobs'),
    refetchInterval: polling ? 2000 : false,
  });
  function reload() { void queryClient.invalidateQueries({ queryKey: ['jobs'] }); }
  useLedgerReload('jobs home', reload);
  const jobs = query.data?.jobs || [];

  useEffect(() => {
    setBusy((current) => {
      let changed = false;
      const next = { ...current };
      for (const job of jobs) {
        if (current[job.key] && !job.running) {
          next[job.key] = false;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [jobs]);

  useEffect(() => {
    if (!logJob) return;
    const controller = new AbortController();
    setLines([]);
    setLogError('');
    void (async () => {
      try {
        const response = await fetch(`/api/jobs/${logJob.key}/logs`, { credentials: 'include', signal: controller.signal });
        if (!response.ok || !response.body) throw new Error(`Logs failed (${response.status})`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const chunk = await reader.read();
          if (chunk.done) break;
          buffer += decoder.decode(chunk.value, { stream: true });
          const parts = buffer.split('\n');
          buffer = parts.pop() || '';
          const messages = parts.map((line) => line.trim()).filter(Boolean).map((line) => JSON.parse(line) as { type?: string; message?: string }).filter((event) => event.type === 'line' && event.message).map((event) => event.message as string);
          if (messages.length) setLines((current) => [...current, ...messages]);
        }
      } catch (err) {
        if (!controller.signal.aborted) setLogError(err instanceof Error ? err.message : 'Logs failed');
      }
    })();
    return () => controller.abort();
  }, [logJob]);

  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [lines]);

  async function run(job: Job) {
    setBusy((current) => ({ ...current, [job.key]: true }));
    const result = await notice.run(`Running ${job.name}`, () => apiPost<{ running: boolean }>(`/api/jobs/${job.key}/run`, {}), (row) => row.running ? `${job.name} started` : `${job.name} finished`);
    if (!result?.running) setBusy((current) => ({ ...current, [job.key]: false }));
    if (result) reload();
    else queryClient.setQueryData<{ jobs: Job[] }>(['jobs'], (current) => current ? {
      ...current,
      jobs: current.jobs.map((row) => row.key === job.key ? { ...row, lastOk: false, lastMessage: 'Job failed' } : row),
    } : current);
  }

  const jobColumns = [
    { key: 'name', label: 'Job', text: (job: Job) => job.name },
    { key: 'cadence', label: 'Cadence', text: (job: Job) => job.cadence },
    { key: 'last', label: 'Last run', text: (job: Job) => job.running || busy[job.key] ? 'Running' : (job.lastRunAt || 'not run'), sort: (job: Job) => job.lastRunAt || '' },
    { key: 'message', label: 'Message', text: (job: Job) => job.running || busy[job.key] ? 'Running' : shownMessage(job) },
  ];
  const shownJobs = applyTableQuery(jobs, search, jobColumns, sort).filter((job) => columnSearch.matches(Object.fromEntries(jobColumns.map((column) => [column.key, String(column.text(job) ?? '')]))));

  return (
    <div>
      <PageHead crumbs={[{ label: 'Operate' }, { label: 'Jobs' }]} title="Jobs" subtitle="Scheduled finance jobs" />
      <div className={logJob ? 'grid items-stretch gap-4 max-md:block xl:grid-cols-[3fr_2fr]' : ''}>
      <div className={`min-w-0 flex-1 ${logJob ? 'max-md:hidden' : ''}`}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input className="h-8 max-w-xs" aria-label="Search jobs" placeholder="Search jobs" value={search} onChange={(event) => setSearch(event.target.value)} />
        <ExcelButton filename="Jobs" headers={['Job', 'Cadence', 'Last run', 'Message']} rows={shownJobs.map((job) => {
          const running = Boolean(job.running || busy[job.key]);
          return [job.name, job.cadence, running ? 'Running' : (job.lastRunAt ? job.lastRunAt.replace('T', ' ').slice(0, 19) : 'not run'), running ? 'Running' : shownMessage(job)];
        })} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <table className="bo-cards min-w-full text-sm">
          <thead><tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <SortHeader className="px-3 py-2 font-medium" label="Job" active={sort?.key === 'name'} dir={sort?.dir || 'asc'} onSort={() => setSort((current) => nextSort(current, 'name'))} />
            <SortHeader className="py-2 font-medium" label="Cadence" active={sort?.key === 'cadence'} dir={sort?.dir || 'asc'} onSort={() => setSort((current) => nextSort(current, 'cadence'))} />
            <SortHeader className="py-2 font-medium" label="Last run" active={sort?.key === 'last'} dir={sort?.dir || 'asc'} onSort={() => setSort((current) => nextSort(current, 'last'))} />
            <SortHeader className="py-2 font-medium" label="Message" active={sort?.key === 'message'} dir={sort?.dir || 'asc'} onSort={() => setSort((current) => nextSort(current, 'message'))} />
            <th className="py-2 pr-3 text-right font-medium"><span className="mr-2">Actions</span><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th>
          </tr>
          {columnSearch.open ? <ColumnFilterInputs columns={jobColumns.map((column) => ({ key: column.key, label: column.label }))} draft={columnSearch.draft} onChange={columnSearch.set} trailing /> : null}
          </thead>
          <tbody>
            {shownJobs.map((job) => {
              const running = Boolean(job.running || busy[job.key]);
              const message = shownMessage(job);
              return (
                <tr key={job.key} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="px-3 py-3" data-label="Job">{job.name}</td>
                  <td className="py-3" data-label="Cadence">{job.cadence}</td>
                  <td className="py-3" data-label="Last run">{running ? <span className="text-blue-600">Running</span> : (job.lastRunAt ? job.lastRunAt.replace('T', ' ').slice(0, 19) : 'not run')}</td>
                  <td className={`max-w-md py-3 pr-3 ${running ? 'text-blue-600' : failed(job) ? 'text-red-600' : 'text-gray-600 dark:text-gray-300'}`} data-label="Message">{running ? 'Running' : message}</td>
                  <td className="py-3 pr-3 text-right" data-label="">
                    <div className="flex justify-end gap-2">
                      <Button size="xs" disabled={running} onClick={() => void run(job)}>{running ? 'Running' : 'Run now'}</Button>
                      <Button size="xs" variant="outline" onClick={() => setLogJob(job)}>View logs</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
      {logJob ? (
        <ContextPanel label="Job" title={logJob.name} subtitle={logJob.cadence} onClose={() => setLogJob(null)}>
          <pre ref={logRef} className="max-h-[28rem] overflow-auto whitespace-pre-wrap font-mono text-xs">{lines.join('\n') || 'Waiting for log lines.'}</pre>
          {logError ? <p className="mt-2 text-sm text-red-400">{logError}</p> : null}
        </ContextPanel>
      ) : null}
      </div>
    </div>
  );
}
